import { useCallback, useRef, useMemo } from 'react';
import {
  VIEW_CONFIGS,
  getNextView,
  getPreviousView,
  type ViewId,
} from '../constants/viewConfig';

// Re-export ViewId as ActiveView for backwards compatibility
export type ActiveView = ViewId;

interface ViewToggleProps {
  activeView: ActiveView;
  onToggle: (view: ActiveView) => void;
  disabled?: boolean;
}

export function ViewToggle({ activeView, onToggle, disabled = false }: ViewToggleProps) {
  // Create refs for each view tab dynamically
  const tabRefs = useRef<Map<ViewId, HTMLButtonElement | null>>(new Map());

  // Pre-create ref callbacks for each view to avoid creating functions in render loop
  const refCallbacks = useMemo(() => {
    const callbacks = new Map<ViewId, (el: HTMLButtonElement | null) => void>();
    VIEW_CONFIGS.forEach(config => {
      callbacks.set(config.id, (el: HTMLButtonElement | null) => {
        tabRefs.current.set(config.id, el);
      });
    });
    return callbacks;
  }, []);

  // Handle keyboard navigation between tabs (WAI-ARIA tabs pattern)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, currentViewId: ActiveView) => {
      if (disabled) return;

      let targetViewId: ActiveView | null = null;

      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        targetViewId = getPreviousView(currentViewId);
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        targetViewId = getNextView(currentViewId);
      }

      if (targetViewId) {
        e.preventDefault();
        onToggle(targetViewId);
        tabRefs.current.get(targetViewId)?.focus();
      }
    },
    [disabled, onToggle]
  );

  const baseClasses = 'px-4 py-2 text-sm font-medium rounded-md transition-all duration-normal min-h-[44px] min-w-[60px]';
  const activeClasses = 'bg-primary text-white';
  const inactiveClasses = 'text-gray-400 hover:text-white hover:bg-white/5';
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';

  return (
    <div
      role="tablist"
      aria-label="View selection"
      className="flex bg-bg-surface rounded-lg p-1"
    >
      {VIEW_CONFIGS.map((viewConfig) => {
        const isActive = activeView === viewConfig.id;
        return (
          <button
            key={viewConfig.id}
            ref={refCallbacks.get(viewConfig.id)}
            role="tab"
            id={viewConfig.tabId}
            aria-selected={isActive}
            aria-controls={viewConfig.panelId}
            tabIndex={isActive ? 0 : -1}
            onClick={() => !disabled && onToggle(viewConfig.id)}
            onKeyDown={(e) => handleKeyDown(e, viewConfig.id)}
            disabled={disabled}
            className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses} ${disabledClasses}`}
          >
            {viewConfig.label}
          </button>
        );
      })}
    </div>
  );
}
